import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { useContext, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useUpdateAccountMutation,
  useUpsertPayerAccountFeesMutation,
} from "store/queries/account";

import { validaTeCustomPaymentPlanSettings } from "../helpers/helpers";
import { mapFormToRequestBody } from "../helpers/mapFormToRequestBody";

export function SubmitEdit({
  id,
  initialAccountLocations = [],
}: {
  id: string;
  initialAccountLocations?: Array<{ id: string; name?: string }>;
}) {
  const [useUpdateAccount] = useUpdateAccountMutation();
  const [upsertPayerAccountFees] = useUpsertPayerAccountFeesMutation();
  const { accountRefetch } = useUserWithAccount();
  const [buttonLoading, setButtonLoading] = useState(false);
  const { current: toastElement } = useContext(ToastContext);
  const {
    getValues,
    formState: { isValid },
    watch,
  } = useFormContext();
  const paymentPlanCustomSettingsField = watch("paymentPlanCustomSettings");
  const navigate = useNavigate();

  const updateHospital = async () => {
    const formValues = getValues();
    const requestBody = await mapFormToRequestBody(formValues, {
      initialAccountLocations,
    });
    setButtonLoading(true);

    try {
      // Update the main account
      await useUpdateAccount({ ...requestBody, id }).unwrap();

      // We always update payer account fees (even if empty array to clear existing fees)
      const formattedPayerAccountFees = (formValues.payerAccountFees || []).map(
        (fee: any) => ({
          payerId: parseInt(fee.payerId, 10),
          type: fee.type || "ClearPatientFee",
          valueInPercentage: parseFloat(fee.valueInPercentage) || 0,
        })
      );

      await upsertPayerAccountFees({
        accountId: id,
        payerAccountFees: formattedPayerAccountFees,
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Account updated",
        detail: "Account updated successfully",
      });
      setButtonLoading(false);
      navigate("/hospitals");
      accountRefetch();
    } catch (error) {
      console.error(error);
      setButtonLoading(false);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "An error has occurred attempting to update the account",
      });
    }
  };

  const submitButtonDIsabled = useMemo(() => {
    if (!isValid) return true;
    if (paymentPlanCustomSettingsField?.length) {
      const isCustomSettingsValid = validaTeCustomPaymentPlanSettings({
        settings: paymentPlanCustomSettingsField,
      });
      if (!isCustomSettingsValid) return true;
    }
    return false;
  }, [isValid, paymentPlanCustomSettingsField]);

  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Hospital"
          onClick={updateHospital}
          disabled={submitButtonDIsabled}
          loading={buttonLoading}
        />
      </div>
    </ItemBar>
  );
}
